import type {
  ProjectSettings,
  ProjectWithInterviewCount,
} from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { type FC, useState } from "react";
import { useIsFeatureFlagEnabled } from "../../../../helpers/featureFlags/hooks.js";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";
import { JobOpeningPreInterviewFormSetting } from "./jobOpeningPreInterviewFormSetting.js";
import { JobOpeningSchedulingSetting } from "./jobOpeningSchedulingSetting.js";

interface JobOpeningDetailsInputsProps {
  location: string;
  pay: string;
  hours: string;
  onLocationChange: (value: string) => void;
  onPayChange: (value: string) => void;
  onHoursChange: (value: string) => void;
  shouldShowSaveButtons?: boolean;
  onSaveLocation?: () => void;
  onSavePay?: () => void;
  onSaveHours?: () => void;
  onCancelLocation?: () => void;
  onCancelPay?: () => void;
  onCancelHours?: () => void;
  onLocationBlur?: () => void;
}

export const JobOpeningDetailsInputs: FC<JobOpeningDetailsInputsProps> = ({
  location,
  pay,
  hours,
  onLocationChange,
  onPayChange,
  onHoursChange,
  shouldShowSaveButtons,
  onSaveLocation,
  onSavePay,
  onSaveHours,
  onCancelLocation,
  onCancelPay,
  onCancelHours,
  onLocationBlur,
}) => {
  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => (
        <>
          <div className="space-y-3">
            <div>
              <Label size={Size.SMALL}>Location</Label>
              <div className="mt-2">
                <Input
                  value={location}
                  onChange={(evt) => {
                    onLocationChange(evt.target.value);
                  }}
                  onBlur={onLocationBlur}
                  placeholder="e.g. In-office 3 days per week in San Francisco, CA or Remote"
                  maxLength={200}
                  inputClassName="max-w-[400px]"
                  isDisabled={!allowed}
                />
              </div>
              {shouldShowSaveButtons && onSaveLocation && onCancelLocation ? (
                <SaveAndCancelButtons
                  onSave={onSaveLocation}
                  onCancel={onCancelLocation}
                />
              ) : null}
            </div>

            <div>
              <Label size={Size.SMALL}>Pay</Label>
              <div className="mt-2">
                <Input
                  value={pay}
                  onChange={(evt) => {
                    onPayChange(evt.target.value);
                  }}
                  placeholder="e.g. $18 per hour or $80,000 - $100,000 per year"
                  maxLength={200}
                  inputClassName="max-w-[400px]"
                  isDisabled={!allowed}
                />
              </div>
              {shouldShowSaveButtons && onSavePay && onCancelPay ? (
                <SaveAndCancelButtons
                  onSave={onSavePay}
                  onCancel={onCancelPay}
                />
              ) : null}
            </div>

            <div>
              <Label size={Size.SMALL}>Hours</Label>
              <div className="mt-2">
                <Input
                  value={hours}
                  onChange={(evt) => {
                    onHoursChange(evt.target.value);
                  }}
                  placeholder="e.g. Full-time, 40 hours per week"
                  maxLength={200}
                  inputClassName="max-w-[400px]"
                  isDisabled={!allowed}
                />
              </div>
              {shouldShowSaveButtons && onSaveHours && onCancelHours ? (
                <SaveAndCancelButtons
                  onSave={onSaveHours}
                  onCancel={onCancelHours}
                />
              ) : null}
            </div>
          </div>
        </>
      )}
    </Can>
  );
};

interface JobOpeningSettingsProps {
  project: ProjectWithInterviewCount;
  onSaveProject: (project: {
    id: string;
    location?: { description: string } | null;
    pay?: { description: string } | null;
    hours?: { description: string } | null;
    settings?: ProjectSettings;
  }) => Promise<void>;
}

export const JobOpeningSettings: FC<JobOpeningSettingsProps> = ({
  project,
  onSaveProject,
}) => {
  const isSchedulingEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.Scheduling,
  );

  const originalLocation = project.location?.description ?? "";
  const originalPay = project.pay?.description ?? "";
  const originalHours = project.hours?.description ?? "";

  const [location, setLocation] = useState(originalLocation);
  const [pay, setPay] = useState(originalPay);
  const [hours, setHours] = useState(originalHours);

  const hasLocationChanges = location !== originalLocation;
  const hasPayChanges = pay !== originalPay;
  const hasHoursChanges = hours !== originalHours;

  const handleSaveLocation = () => {
    void onSaveProject({
      id: project.id,
      location: location ? { description: location } : null,
    });
  };

  const handleSavePay = () => {
    void onSaveProject({
      id: project.id,
      pay: pay ? { description: pay } : null,
    });
  };

  const handleSaveHours = () => {
    void onSaveProject({
      id: project.id,
      hours: hours ? { description: hours } : null,
    });
  };

  const [allowsExistingInterviewsOnly, setAllowsExistingInterviewsOnly] =
    useState(project.settings.link_access === "existing-only");

  const onAllowsExistingInterviewsOnlyChange = (isChecked: boolean) => {
    setAllowsExistingInterviewsOnly(isChecked);
    onSaveProject({
      id: project.id,
      settings: {
        ...project.settings,
        link_access: isChecked ? "existing-only" : "default",
      },
    });
  };

  return (
    <>
      <JobOpeningDetailsInputs
        location={location}
        pay={pay}
        hours={hours}
        onLocationChange={setLocation}
        onPayChange={setPay}
        onHoursChange={setHours}
        shouldShowSaveButtons
        onSaveLocation={hasLocationChanges ? handleSaveLocation : undefined}
        onSavePay={hasPayChanges ? handleSavePay : undefined}
        onSaveHours={hasHoursChanges ? handleSaveHours : undefined}
        onCancelLocation={
          hasLocationChanges
            ? () => {
                setLocation(originalLocation);
              }
            : undefined
        }
        onCancelPay={
          hasPayChanges
            ? () => {
                setPay(originalPay);
              }
            : undefined
        }
        onCancelHours={
          hasHoursChanges
            ? () => {
                setHours(originalHours);
              }
            : undefined
        }
      />

      <JobOpeningPreInterviewFormSetting
        project={project}
        onSaveProject={onSaveProject}
      />

      {isSchedulingEnabled ? (
        <JobOpeningSchedulingSetting
          project={project}
          onSaveProject={onSaveProject}
        />
      ) : null}

      <div className="mt-6">
        <Label size={Size.SMALL} className="mb-2">
          Restrict interview link access
        </Label>
        <Can
          I={PermissionActions.UPDATE}
          a={PermissionSubjects.JOB_OPENINGS}
          passThrough={true}
        >
          {(allowed) => (
            <Checkbox
              isChecked={allowsExistingInterviewsOnly}
              onChange={onAllowsExistingInterviewsOnlyChange}
              label="Only allow pre-registered candidates to interview (not recommended)"
              isDisabled={!allowed}
            />
          )}
        </Can>
        <div className="mt-2 text-sm text-muted-foreground">
          When enabled, you'll need to upload candidates in advance and
          unrecognized candidates will be prevented from interviewing.
        </div>
      </div>
    </>
  );
};
